import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DollarSign, Plus } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';

export default function ExpensesTab({ event, user }) {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: '', amount: '', currency: 'USD' });

  const load = useCallback(async () => {
    try {
      const [e, s] = await Promise.all([
        api.get(`/events/${event.id}/expenses`),
        api.get(`/events/${event.id}/expenses/summary`),
      ]);
      setItems(e.data || []);
      setSummary(s.data || null);
    } catch (err) {
      console.error('Failed to load expenses:', err);
    }
  }, [event.id]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.title.trim() || !form.amount) return toast.error('Title and amount required');
    try {
      await api.post(`/events/${event.id}/expenses`, { title: form.title.trim(), amount: parseFloat(form.amount), currency: form.currency });
      setForm({ title: '', amount: '', currency: 'USD' }); setOpen(false); load(); toast.success('Added');
    } catch (err) {
      console.error('Failed to add expense:', err);
      toast.error('Failed to add expense');
    }
  };

  return (
    <div className="space-y-3" data-testid="expenses-tab">
      {summary && (
        <Card className="rounded-2xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase text-muted-foreground tracking-wider">Total spent</div>
              <div className="font-serif text-3xl font-semibold">${summary.total?.toFixed(2)}</div>
            </div>
            <Button onClick={() => setOpen((v) => !v)} data-testid="expense-toggle-add" className="rounded-xl gradient-fab text-white"><Plus className="h-4 w-4 mr-1" />{open ? 'Close' : 'Add'}</Button>
          </div>
          {summary.balances?.length > 0 && (
            <div className="mt-3 space-y-1">
              {summary.balances.map((b) => (
                <div key={b.user?.id || `bal-${b.user?.email || Math.random()}`} className="flex items-center justify-between text-sm" data-testid={`balance-${b.user?.id}`}>
                  <span className="truncate">{b.user?.name || 'Member'}</span>
                  <span className={`${b.net >= 0 ? 'text-[hsl(var(--success))]' : 'text-destructive'} font-medium`}>{b.net >= 0 ? '+' : '-'}${Math.abs(b.net).toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
      {open && (
        <Card className="rounded-2xl p-3 space-y-2" data-testid="expense-form">
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Dinner" data-testid="expense-title-input" /></div>
            <div><Label>Amount</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} data-testid="expense-amount-input" /></div>
          </div>
          <Button onClick={add} data-testid="expense-add-button" className="rounded-xl gradient-fab text-white">Add expense</Button>
        </Card>
      )}
      {items.length === 0 ? (
        <Card className="rounded-2xl p-6 text-center text-sm text-muted-foreground">No expenses yet.</Card>
      ) : (
        <div className="space-y-2" data-testid="expense-list">
          {items.map((e) => (
            <Card key={e.id} className="rounded-2xl p-3" data-testid={`expense-${e.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-sm">{e.title}</div>
                  <div className="text-xs text-muted-foreground">{new Date(e.created_at).toLocaleDateString()}</div>
                </div>
                <div className="font-semibold flex items-center gap-1"><DollarSign className="h-4 w-4" />{e.amount?.toFixed?.(2)}</div>
              </div>
              {e.splits?.length > 0 && <div className="text-xs text-muted-foreground mt-1">Split among {e.splits.length} · ${(e.splits[0]?.amount || 0).toFixed(2)} each</div>}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
